<template>
  <div class="form__group">
    <label v-if="props.label" :for="fieldId" class="form__label">
      {{ $t(props.label) }}<span v-if="props.required" class="form__required" aria-hidden="true">*</span>
    </label>
    <div v-if="props.topHint" class="form__top-hint">
      {{ $t(props.topHint) }}
    </div>
    <slot></slot>

    <div v-if="error" class="form__error">
      {{ error }}
    </div>
    <div v-if="props.hint" class="form__hint">
      {{ $t(props.hint) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { provide, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { Validation } from '@vuelidate/core';
import { ValidationUtils } from '@/types';

const props = defineProps<{
  id?: string;
  label: string | null | undefined;
  hint?: string;
  topHint?: string;
  required?: boolean;
  v: Validation | null;
}>();

const validation = computed<ValidationUtils>(() => ({
  invalid: props.v?.$error!!,
  touch: () => {
    props.v?.$touch();
  },
  onChange: (value: any) => {
    // clear api errors
    if (props.v?.$errors.find(e => e.$validator === '$externalResults')) {
      props.v.$model = value;
    }
  }
}));

provide("field-validation", validation);

const { t } = useI18n();

const error = computed(() => {
  if (!props.v?.$dirty || !props.v.$errors[0]) {
    return null;
  }

  const firstError = props.v.$errors[0];

  if (firstError.$validator === '$externalResults') {
    return firstError.$message;
  }

  return t(`formError.${firstError.$validator}`, firstError.$params as any);
});
</script>

<script lang="ts">
import { defineComponent } from "vue";
let id = 1;

export default defineComponent({
  provide() {
    return { "field-id": this.fieldId };
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ["id"],
  data() {
    return { fieldId: this.$props.id ?? `ff-${id++}` };
  },
});
</script>
